'use client'

import { memo, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import styled from 'styled-components'
import { useInView } from 'motion/react'
import { MODALS } from '@/context'
import { useModal } from '@/hooks'
import { mq, presets, rgbAlpha, vw } from '@/styles'
import { E_VIDEO_PLATFORM } from '@/schema'
// import { ModalVideo } from '@/components/modals/ModalVideo'

const DynamicModalVideo = dynamic(
  () =>
    import('@/components/project/modals/ModalVideo').then(
      (mod) => mod.ModalVideo
    ),
  { ssr: false }
)

const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false })

type TypeContentVideo = {
  col?: {
    from: number
    expand: number }
  row?: {
    from: number
    expand: number
  }
  medium: {
    type: 'video' | string
    alt?: string
    short_video?: string | number
    long_video?: string | number
    platform: E_VIDEO_PLATFORM
  }
  videoType?: E_VIDEO_PLATFORM
  caption?: string
  type: 'media' | string
}

export interface IContentVideo {
  data: TypeContentVideo
  longModal?: boolean
  playsinline?: boolean
  modalActive?: boolean
  insideModal?: boolean
}

// const hexagonIcon = {
//   type: 'svg' as const,
//   src: '/images/svg/i--hexagon-rounded.svg',
//   alt: 'Hexagon icon',
//   size: { width: 63, height: 69 }
// }

export const ContentVideo = memo(({ data, longModal = false, modalActive = false, playsinline = false, insideModal = false }: IContentVideo) => {
  console.log('data video', data)
  const { medium: { long_video, short_video }, videoType } = data
  const [playing, setPlaying] = useState(!longModal)
  const ref = useRef(null)
  const { toggle } = useModal(MODALS.VIDEO)

  const inView = useInView(ref, {
    margin: '0px',
    amount: 0,
  })

  const url = `https://www.${videoType?.toLowerCase()}.com/${videoType === E_VIDEO_PLATFORM.YOUTUBE ? 'watch?v=' : ''}${longModal ? long_video : short_video}`

  useEffect(() => {
    if (longModal) return
    setPlaying(inView)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  useEffect(() => {
    if (longModal) setPlaying(modalActive)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalActive])

  return (
    <>
      <VideoContent
        ref={ref}
        $longModal={longModal}
        className='figure--video'
        $insideModal={insideModal}
        $isModalActive={modalActive}
      >
        <ReactPlayer
          url={url}
          playsinline={playsinline}
          controls={longModal}
          volume={longModal ? 0.5 : 0}
          muted={!longModal}
          loop={!longModal}
          playing={playing}
        />
        {long_video && !longModal && (
          <PlayIcon onClick={toggle}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src='/images/svg/i--hexagon-rounded.svg'
              alt='Icon Hexagon'
              width='63'
              height='69'
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className='xs'
              src='/images/svg/i--hexagon-rounded.svg'
              alt='Icon Hexagon small'
              width='63'
              height='69'
            />
            <span>Play</span>
          </PlayIcon>
        )}
      </VideoContent>
      {long_video && !longModal && <DynamicModalVideo data={data} />}
    </>
  )
})

const VideoContent = styled.figure<{ $longModal: IContentVideo['longModal'], $insideModal: IContentVideo['insideModal'], $isModalActive: IContentVideo['modalActive'] }>`
  height: auto;
  position: relative;
  width: 100%;

  ${mq.greaterThan('desktop-large')} {
    height: 600px;
  }

  img {
    width: 100%;
  }

  figcaption {
    ${presets.font12_1}
    border-bottom: ${rgbAlpha('--color-black', 0.1)} 1px solid;
    padding-bottom: ${vw(20, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding-bottom: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding-bottom: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding-bottom: 20px;
    }
  }

  > div {
    height: ${({ $longModal }) =>
    $longModal ? '56.25vw' : `${vw(232, 'mobile')}`} !important;
    pointer-events: ${({ $insideModal, $isModalActive, $longModal }) =>
    $insideModal
      ? $isModalActive
        ? 'auto'
        : 'none'
      : $longModal
        ? 'auto'
        : 'none'};
    width: 100% !important;

    ${mq.greaterThan('nexus7')} {
      height: ${({ $longModal }) =>
    $longModal ? '56.25vw' : `${vw(232, 'tablet')}`} !important;
    }

    ${mq.greaterThan('tablet')} {
      height: ${({ $longModal }) =>
    $longModal ? '56.25vw' : `${vw(540, 'desktop')}`} !important;
      max-height: 100vh;
    }

    ${mq.greaterThan('desktop-large')} {
      height: ${({ $longModal }) =>
    $longModal ? '56.25vw' : '540px'} !important;
      max-height: 100vh;
    }
  }

  iframe {
    aspect-ratio: 16 / 9;
    height: auto !important;
    width: 100% !important;

    @media (max-aspect-ratio: 16/9) {
      height: 100% !important;
    }

    ${mq.greaterThan('desktop-large')} {
      height: 100% !important;
      width: 100% !important;
    }
  }
`

const PlayIcon = styled.button`
  align-items: center;
  border-radius: 100%;
  display: flex;
  height: ${vw(90, 'mobile')};
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: height 300ms ease, width 300ms ease;
  width: ${vw(80, 'mobile')};
  z-index: 2;

  ${mq.greaterThan('nexus7')} {
    height: ${vw(90, 'tablet')};
    width: ${vw(80, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    height: ${vw(120, 'desktop')};
    width: ${vw(110, 'desktop')};

    &:hover {
      img {
        &:first-child {
          width: ${vw(110, 'desktop')};
        }
        &:nth-child(2) {
          width: ${vw(82, 'desktop')};
        }
      }
    }
  }

  ${mq.greaterThan('desktop')} {
    height: 120px;
    width: 110px;

    &:hover {
      img {
        &:first-child {
          width: 110px;
        }
        &:nth-child(2) {
          width: 82px;
        }
      }
    }
  }

  span {
    ${presets.font10}
    color: var(--color-white);
    left: 50%;
    position: absolute;
    text-transform: uppercase;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
  }

  img {
    left: 50%;
    opacity: 0.5;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    transition: 300ms width ease-in-out;
    user-select: none;
    width: ${vw(73, 'mobile')};

    &:first-child {
      transition-delay: 150ms;
    }

    ${mq.greaterThan('nexus7')} {
      width: ${vw(73, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      width: ${vw(91, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      width: 91px;
    }

    &.xs {
      opacity: 1;
      width: ${vw(50, 'mobile')};
      z-index: 2;

      ${mq.greaterThan('nexus7')} {
        width: ${vw(50, 'tablet')};
      }

      ${mq.greaterThan('desktop')} {
        width: 63px;
      }
    }
  }
`
